import { useState, useEffect, useCallback, useMemo } from "react";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";

const STORAGE_KEY = "flowboard-column-visibility";

function readSavedVisibility(statusNames: string[]) {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      return null;
    }
    const parsed = JSON.parse(saved) as Record<string, boolean>;
    const result: Record<string, boolean> = {};
    statusNames.forEach((name) => {
      result[name] = parsed[name] ?? true;
    });
    return result;
  } catch {
    return null;
  }
}

export function useColumnVisibility(availableStatuses: AppointmentStatus[]) {
  // Primitive key so parents can safely use `data ?? []` without a new [] reference each render
  // (which would retrigger effects and loop on setVisibleColumns(new Set())).
  const statusNamesKey = availableStatuses.map((status) => status.name).join("\u0001");

  const statusNames = useMemo(
    () => availableStatuses.map((status) => status.name),
    // Do not depend on `availableStatuses` reference — Flowboard uses `data ?? []`, a new [] each render while loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusNamesKey]
  );
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => new Set());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (statusNames.length === 0) {
      setHydrated(false);
      setVisibleColumns(new Set());
      return;
    }

    setHydrated(false);

    const savedVisibility = readSavedVisibility(statusNames);
    const nextSet = new Set<string>();

    if (savedVisibility) {
      statusNames.forEach((name) => {
        if (savedVisibility[name]) {
          nextSet.add(name);
        }
      });
    }

    if (nextSet.size === 0) {
      statusNames.forEach((name) => nextSet.add(name));
    }

    setVisibleColumns(nextSet);
    setHydrated(true);
  }, [statusNamesKey, statusNames]);

  useEffect(() => {
    if (!hydrated || statusNames.length === 0) return;

    try {
      const visibilityMap: Record<string, boolean> = {};
      statusNames.forEach((name) => {
        visibilityMap[name] = visibleColumns.has(name);
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibilityMap));
    } catch {
      // Ignore errors (e.g., localStorage disabled)
    }
  }, [visibleColumns, statusNames]);

  const toggleColumn = useCallback((statusName: string) => {
    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(statusName)) {
        newSet.delete(statusName);
      } else {
        newSet.add(statusName);
      }
      return newSet;
    });
  }, []);

  const isColumnVisible = useCallback((statusName: string) => {
    return visibleColumns.has(statusName);
  }, [visibleColumns]);

  return {
    visibleColumns,
    toggleColumn,
    isColumnVisible,
  };
}
